import { SortingHeaderButton } from '@/components/ui/DataTable/SortingHeaderButton'
import { MarkdownDocumentationHover } from '../MdTextComponent/MarkdownDocumentationHover'
import { CellContext, Column, ColumnDef } from '@tanstack/react-table'
import { RenderCellV2 } from './RenderCellV2'
import { getSortingFnV2 } from './sortingFunctionsV2'
import { DataTableV2Column, DataTableV2UISchema } from './types'

/**
 * Renders the column header with optional sorting and tooltip
 */
const RenderColumnHeader = <T,>({
  column,
  title,
  description,
  sortingEnabled,
}: {
  column: Column<T>
  title: string
  description?: string
  sortingEnabled?: boolean
}) => {
  if (sortingEnabled) {
    return (
      <SortingHeaderButton<T> column={column} description={description}>
        {title}
      </SortingHeaderButton>
    )
  }

  return (
    <div className="flex items-center gap-x-1 font-bold">
      {description ? (
        <MarkdownDocumentationHover documentation={description}>
          <span className="cursor-default">{title}</span>
        </MarkdownDocumentationHover>
      ) : (
        <span>{title}</span>
      )}
    </div>
  )
}

/**
 * Gets the width from column data display configuration
 */
const getColumnWidth = (col: DataTableV2Column): number | undefined => {
  return col.dataDisplay.width
}

/**
 * Creates column definitions for the table based on the V2 schema
 */
export function getColumnsV2<T>(uiSchema: DataTableV2UISchema): ColumnDef<T>[] {
  // Note: We keep ALL columns here (including hidden ones) so that sorting can work on hidden columns.
  // Column visibility is managed via TanStack Table's columnVisibility state in DataTableV2Content.
  const accessorColumns = uiSchema.columns.map((col) => ({
    id: col.id,
    accessorKey: col.id,
    header: ({ column }: { column: Column<T> }) => (
      <RenderColumnHeader
        column={column}
        title={col.title}
        description={col.description}
        sortingEnabled={col.isSortingEnabled}
      />
    ),
    cell: (info: CellContext<T, unknown>) => <RenderCellV2 dataDisplay={col.dataDisplay} value={info.getValue()} />,
    sortingFn: getSortingFnV2<T>(col.id, col.dataDisplay),
    size: getColumnWidth(col),
  }))

  // // Row actions column
  // const rowActionColumn = uiSchema.rowActions?.length
  //   ? {
  //       id: 'rowActions',
  //       cell: (info: CellContext<T, unknown>) => (
  //         <RowActionsV2Menu
  //           info={info}
  //           rowActions={uiSchema.rowActions ?? []}
  //           groupConfig={uiSchema.tableConfig.groupConfig}
  //         />
  //       ),
  //       size: 48,
  //     }
  //   : undefined

  // return rowActionColumn ? [...accessorColumns, rowActionColumn] : accessorColumns

  return accessorColumns
}
